import { useEffect, FC, useRef, useState } from 'react'

import { Route, Switch, Redirect } from 'react-router-dom'

import AppRoutes from 'app/AppRoutes'
import DocumentUploader from 'noauth/pages/DocumentUploader'
import LoginLayout from 'noauth/pages/Login/LoginLayout'
import LoginManager from 'noauth/pages/Login/LoginManager'

import {
  getStoredToken,
  decodeToken,
  isTokenValid,
  tokenTimeRemaining,
  getQueryUTM,
  removeUTMQueryParams,
  PEACH_UTM,
  renewSession,
} from 'core/actions/helpers/tokenHelpers'
import getLoanTypes from 'core/actions/loanTypes/getLoanTypes'
import getPermissions from 'core/actions/permissions/getPermissions'
import { useSetGlobalValue } from 'core/badGlobalDoNotUse'
import { getOverride } from 'core/components/PeachOverrides/storage'
import { R } from 'core/helpers'
import { storeSession, useAppDispatch } from 'core/hooks'
import { useCompanyId } from 'core/stores'

import LoginRedirect from './loginRedirect/LoginRedirect'

const App: FC = () => {
  const dispatch = useAppDispatch()
  const companyId = useCompanyId()
  const utm = getQueryUTM()

  const forceUnhandledException = getOverride('forceUnhandledException')

  const [token, setToken] = useState(getStoredToken())
  const [isValidToken, setIsValidToken] = useState(isTokenValid(token))
  const isRenewing = useRef(false)

  const { personId: sessionPersonId } = decodeToken(token)

  // this is a hack to allow reducers and other functions access to
  // current user's personId
  useSetGlobalValue('sessionPersonId', sessionPersonId)

  useEffect(() => {
    // Only set up interval if we have a valid token to start with
    if (!isTokenValid(token)) {
      if (isValidToken) {
        setIsValidToken(false)
      }
      return
    }

    let isMounted = true

    const checkInterval = setInterval(() => {
      // It's possible that the token gets updated from another tab.
      // This ensures that we always have the latest token by directly
      // fetching it from the local storage.
      const currentToken = getStoredToken()
      const stillValid = isTokenValid(currentToken)

      if (stillValid !== isValidToken) {
        setToken(currentToken)
        setIsValidToken(stillValid)
        return
      }

      // Check if the token is expiring in the next 3 minutes
      if (stillValid && tokenTimeRemaining(currentToken) < 1000 * 60 * 3 && !isRenewing.current) {
        console.info('Token expiring soon. Renewing token')
        isRenewing.current = true
        void (async () => {
          if (!isMounted) return
          const success = await renewSession()

          if (isMounted) {
            if (success) {
              const newToken = getStoredToken()
              setToken(newToken)
              setIsValidToken(isTokenValid(newToken))
            } else {
              // If renewal fails, the token might have been cleared.
              // We need to update the state to reflect this.
              const currentToken = getStoredToken()
              setToken(currentToken)
              setIsValidToken(isTokenValid(currentToken))
            }
            isRenewing.current = false
          }
        })()
      } else if (!stillValid) {
        console.info('Token expired. Redirecting to login')
        setToken(undefined)
        setIsValidToken(false)
      }
    }, 5000)

    return () => {
      isMounted = false
      clearInterval(checkInterval)
    }
  }, [token, isValidToken])

  useEffect(() => {
    // The clearSavedSession() function in loginRedirectHelpers.js
    // will automatically clear the stored UTM values if the user has
    // an invalid session so we only need to store the provided UTM if the
    // user arrives with a valid session.
    if (!R.isNil(utm) && !R.isEmpty(utm) && isValidToken) {
      storeSession(PEACH_UTM, utm)
      removeUTMQueryParams()
    }
  }, [utm, isValidToken])

  /**
   * Fetching all loan types here since it only needs to loaded once.
   */
  useEffect(() => {
    if (companyId && isValidToken) {
      void dispatch(getLoanTypes({ companyId, key: 'GetLoanTypes' }))
      void dispatch(getPermissions({ key: 'GetPermissions' }))
    }
  }, [companyId, dispatch, isValidToken])

  // istanbul ignore next
  if (forceUnhandledException) throw Error('forceUnhandledException')

  return (
    <Switch>
      {/* routes where it doesn't matter if you're logged in */}
      <Route path='/login'>
        <LoginLayout>
          <LoginManager />
        </LoginLayout>
      </Route>

      {/*
      this route enables customers to link to our app and opt-into sending the user directly into the SSO flow.
      we don't do this for all /login routes to avoid getting the user into a redirect loop of
      in-app -> click sign-out -> redirect to /login -> redirect to SSO -> redirected back into app -> oops we're signed in again
      */}

      <Route path='/sso-login'>
        <Redirect to='/login?redirect=immediate' />
      </Route>

      <Route path='/document-uploader/:uploadDocumentLinkKey'>
        <DocumentUploader />
      </Route>

      <Route>
        {isValidToken ?
          <AppRoutes sessionPersonId={sessionPersonId} />
        : <LoginRedirect />}
      </Route>
    </Switch>
  )
}

export default App
